import { Dispatch } from '@reduxjs/toolkit';
import axios, { AxiosInstance, AxiosResponse, AxiosError, AxiosRequestConfig } from 'axios';

import { notify } from 'components/Common/Toast';
import { apiKey, authURL, baseURL, tenantName } from 'config/apiConfig';
import { resetState } from 'redux/action/resetState';
import { extractErrorMessage } from 'utils/helpers';

const refreshTokenExceptionPaths = ['/validate2FA', '/request2FAPin'];

interface ErrorResponse extends AxiosRequestConfig {
    _retry?: boolean;
}
interface ExtendedRequestConfig extends AxiosRequestConfig {
    hideToast?: boolean;
}

interface ExtendError extends AxiosError {
    response: any;
    config: ExtendedRequestConfig;
}

const HANDLED_ERROR_MESSAGES = [
    'the user is not registered',
    'the user is already verified',
    'username already exist',
    'invalid pin',
    'incorrect username and password combination.',
    'bad credentials',
    'no user is found.',
    'there was a problem proxying the request',
] as const;
interface Token {
    app_name: string | null;
    tenant_name: string;
    user_type: string;
    user_name: string;
    scope: string[];
    last_signon_timestamp: number;
    ati: string;
    exp: number;
    uuid: string;
    authorities: string[];
    jti: string;
    client_id: string;
}

export const parseJWT = (token: string) => {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) {
            throw new Error('Invalid token format');
        }
        const base64Url = parts[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(function (c) {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                })
                .join('')
        );

        return JSON.parse(jsonPayload);
    } catch (e) {
        console.log('Error occurred while parsing token.', e);
        return null;
    }
};
export const isTokenExpired = (token: Token) => {
    if (!token?.exp) return true;
    const currentTimestamp = Math.floor(Date.now() / 1000);
    return token.exp < currentTimestamp;
};

class ApiClient {
    private client: AxiosInstance;
    private token: string | undefined;
    private refreshToken: string | undefined;
    private dispatch: Function | undefined;

    constructor(baseURL: string, headers?: {}) {
        this.client = axios.create({
            baseURL,
            headers: {
                'Content-Type': 'application/json',
                'api-key': apiKey,
                tenantName: tenantName,
                ...headers,
            },
        });
        this.handleRequest = this.handleRequest.bind(this);
        this.handleRequestError = this.handleRequestError.bind(this);
        this.handleSuccessResponse = this.handleSuccessResponse.bind(this);
        this.handleErrorResponse = this.handleErrorResponse.bind(this);
        this.refreshAccessToken = this.refreshAccessToken.bind(this);
        this.setDispatch = this.setDispatch.bind(this);
        this.clearTokens = this.clearTokens.bind(this);

        // Interceptors to handle request, response, and errors
        this.client.interceptors.request.use(this.handleRequest, this.handleRequestError);

        this.client.interceptors.response.use(this.handleSuccessResponse, this.handleErrorResponse);
    }

    clearTokens() {
        this.token = undefined;
        this.refreshToken = undefined;
        localStorage.removeItem('token');
        localStorage.removeItem('userAttempts');
    }

    private handleRequest(config: any) {
        if (this?.token) {
            // Set token in the Authorization header
            config.headers.Authorization = `Bearer ${this.token}`;
        }
        if (config.headers?.hideToast) {
            config.hideToast = config.headers.hideToast;
            delete config.headers.hideToast;
        }
        return config;
    }

    private handleRequestError(error: any) {
        return Promise.reject(error);
    }

    private handleSuccessResponse(response: AxiosResponse) {
        // You can customize the success response handling here
        return response.data;
    }

    private isGenericError(message: string): boolean {
        const lowerMessage = message.toLowerCase();
        const errorKeywords = ['404', 'something went wrong', 'bad request', 'timestamp', 'no body'];
        return (
            errorKeywords.some((keyword) => lowerMessage.includes(keyword)) ||
            (lowerMessage.includes('internal server error') && !this.isMessageHandled(lowerMessage))
        );
    }

    private isMessageHandled(message: string): boolean {
        return HANDLED_ERROR_MESSAGES.some((msg) => message.toLowerCase().includes(msg.toLowerCase()));
    }

    private async handleErrorResponse(error: ExtendError) {
        const hideToast = error?.config?.hideToast;
        if (error?.response && !hideToast) {
            // if the response is invalid credentials means login is attempted so no need to call the refresh token api
            if (error?.response?.data?.statusMessage === 'invalid credentials') {
                return notify('Invalid credentials', 'error');
            }
            if (error.response?.data?.code === 'SECURITY') {
                return notify(error.response?.data?.statusMessage, 'error');
            }
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            console.error('Request failed with response data:', error.response.data);
            console.error('Status code:', error.response.status);
            const originalRequest: ErrorResponse = error.config;
            const fullUrl = originalRequest?.url || '';
            const path = new URL(fullUrl, originalRequest?.baseURL || '').pathname;
            const { Authorization, authorization } = originalRequest.headers || {};
            const _resMessage = error?.response?.data?.message || '';
            const skipRefreshToken = refreshTokenExceptionPaths.some(ex => path.endsWith(ex));
            if (
                (Authorization || authorization) &&
                (error.response.status === 401 || _resMessage.includes('401')) &&
                !originalRequest._retry &&
                !skipRefreshToken
            ) {
                originalRequest._retry = true;
                const res: any = await this.refreshAccessToken();
                if (!res) {
                    return Promise.reject(error);
                }
                this.client.defaults.headers.common['Authorization'] = 'Bearer ' + res?.access_token;
                return this.client.request(originalRequest);
            } else {
                // we are passing the string after authorization because if backend sends 401 then we will check for the refresh token
                const msg = extractErrorMessage(error.response?.data?.message || '');
                if (msg) {
                    if (
                        msg === 'The Daily MAXIMUM_AMOUNT limit was exceeded + DAILY_LIMIT_MAX_AMOUNT_EXCEEDED' ||
                        msg === 'For Money Container ID : 33625 + INSUFFICIENT_BALANCE'
                    ) {
                        return notify('The Daily maximum amount limit was exceeded', 'error');
                    } else if (this.isGenericError(msg)) {
                        return notify('Service unavailable. Please try again later.', 'error');
                    }
                    return notify(msg, 'error');
                }
                const data = error.response?.data;
                const additionalStatusMessage = data?.contextResponse?.additionalStatusMessage;
                const status = data?.status;
                const responseMessage = data?.error;
                const errorMessage = error?.message;
                if (additionalStatusMessage) {
                    if (additionalStatusMessage === 'No user is found.') {
                        notify('User not found. Please verify you entered the correct Email.', 'error');
                    } else {
                        notify(additionalStatusMessage, 'error');
                    }
                } else if (responseMessage) {
                    notify(responseMessage, 'error');
                } else if (errorMessage) {
                    notify(errorMessage, 'error');
                } else if (status) {
                    notify(status, 'error');
                } else {
                    notify('Something went wrong!', 'error');
                }
            }
            return Promise.reject(error);
        } else if (error.request) {
            // The request was made but no response was received
            console.error('Request made but no response received:', error.request);
            return error;
        } else {
            // Something happened in setting up the request that triggered an Error
            console.error('Error setting up the request:', error.message);
        }

        // You can throw a custom error or handle it as needed
        throw error;
    }

    // Set the token dynamically
    setToken(token: string | undefined, refreshToken?: string | undefined) {
        this.token = token || this.token;
        this.refreshToken = refreshToken || this.refreshToken;
    }

    private async refreshAccessToken() {
        if (!this?.refreshToken) {
            throw new Error('Refresh token is required');
        }
        const parsedToken = parseJWT(this.refreshToken);
        if (isTokenExpired(parsedToken) && this.dispatch) {
            notify('Session expired, Please login again.', 'error');
            localStorage.removeItem('token');
            await this.dispatch(resetState());
            return;
        }
        try {
            const params = new URLSearchParams();
            params.append('grant_type', 'refresh_token');
            params.append('refresh_token', this.refreshToken);

            const response: any = await this.post(`${authURL}/auth/oauth/token`, params.toString(), {
                'Content-Type': 'application/x-www-form-urlencoded',
            });
            const { access_token, refresh_token } = response;
            // dispatching action to stop the refresh token call when user refreshes window and token is expired
            // now it will update the auth info in redux with updated tokens
            localStorage.setItem('token', JSON.stringify(response));
            access_token && this.setToken(access_token, refresh_token);
            return response;
        } catch (error: any) {
            console.error('Error refreshing token:', error?.message);
            // Handle token refresh error as needed
        }
    }
    setDispatch(dispatch: Dispatch) {
        this.dispatch = dispatch;
    }
    // Example GET request
    async get(endpoint: string, options?: any) {
        try {
            const response = await this.client.get(endpoint, options);
            return response;
        } catch (error) {
            // The error will be caught by the interceptor, so no need to handle it here
            throw error;
        }
    }

    // Example POST request
    async post(endpoint: string, data?: any, headers?: {}) {
        try {
            const response = await this.client.post(endpoint, data, { headers });
            return response;
        } catch (error) {
            // The error will be caught by the interceptor, so no need to handle it here
            throw error;
        }
    }

    // Add more methods for different HTTP methods as needed
    async delete(endpoint: string, data?: any) {
        try {
            return this.client.delete(endpoint, data);
        } catch (error) {
            throw error;
        }
    }

    async put(endpoint: string, data?: any, headers?: {}) {
        try {
            return this.client.put(endpoint, data, { headers });
        } catch (error) {
            throw error;
        }
    }
}
// Usage
const client = new ApiClient(baseURL);
export const authClient = new ApiClient(authURL, {
    'Content-Type': 'application/x-www-form-urlencoded',
});

export default client;
